import React, { useState } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useWeb3 } from '../context/Web3Context';
import { useAuth } from '../context/AuthContext';
import './AdminArtistIdManager.css';

const AdminArtistIdManager = () => {
  const { account } = useWeb3();
  const { isAuthenticated } = useAuth();
  const [walletAddress, setWalletAddress] = useState('');
  const [artistInput, setArtistInput] = useState('');
  const [artistName, setArtistName] = useState('');
  const [loading, setLoading] = useState(false);
  const [artistIds, setArtistIds] = useState([]);

  const getBasePath = () => {
    const wallet = account || localStorage.getItem('walletAddress');
    return (!isAuthenticated && wallet) ? '/test' : '/admin';
  };

  const getTestModeParams = () => {
    const wallet = account || localStorage.getItem('walletAddress');
    return (!isAuthenticated && wallet) ? { params: { walletAddress: wallet } } : {};
  };

  const handleLoad = async () => {
    if (!walletAddress.trim()) {
      toast.error('Wallet address is required');
      return;
    }

    try {
      setLoading(true);
      const basePath = getBasePath();
      const response = await api.get(
        `${basePath}/users/${walletAddress.trim().toLowerCase()}/artist-ids`,
        getTestModeParams()
      );
      setArtistIds(response.data.artistIds || []);
    } catch (error) {
      console.error('Load artist IDs error:', error);
      toast.error(error.response?.data?.message || 'Failed to load artist IDs');
      setArtistIds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!walletAddress.trim()) {
      toast.error('Wallet address is required');
      return;
    }

    if (!artistInput.trim()) {
      toast.error('Artist link or ID is required');
      return;
    }

    try {
      setLoading(true);
      const basePath = getBasePath();
      const payload = {
        artistLink: artistInput.trim(),
        artistName: artistName.trim() || undefined
      };

      const response = await api.post(
        `${basePath}/users/${walletAddress.trim().toLowerCase()}/artist-ids`,
        payload,
        getTestModeParams()
      );

      toast.success(response.data.message || 'Artist ID added');
      setArtistIds(response.data.artistIds || []);
      setArtistInput('');
      setArtistName('');
    } catch (error) {
      console.error('Add artist ID error:', error);
      toast.error(error.response?.data?.message || 'Failed to add artist ID');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (artistId) => {
    if (!walletAddress.trim()) {
      toast.error('Wallet address is required');
      return;
    }

    if (!window.confirm(`Remove artist ID ${artistId}?`)) {
      return;
    }

    try {
      setLoading(true);
      const basePath = getBasePath();
      const response = await api.delete(
        `${basePath}/users/${walletAddress.trim().toLowerCase()}/artist-ids/${artistId}`,
        getTestModeParams()
      );
      toast.success(response.data.message || 'Artist ID removed');
      setArtistIds(response.data.artistIds || []);
    } catch (error) {
      console.error('Remove artist ID error:', error);
      toast.error(error.response?.data?.message || 'Failed to remove artist ID');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-artist-id-manager">
      <div className="manager-header">
        <h2>Assign Artist IDs to Users</h2>
        <p className="manager-note">
          Paste a Spotify artist link/URI or artist ID. Track links are supported and will be resolved
          to the primary artist if Spotify API keys are configured.
        </p>
      </div>

      <form className="artist-form" onSubmit={handleAdd}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="artist-wallet">User Wallet Address *</label>
            <input
              id="artist-wallet"
              type="text"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder="0x..."
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="artist-link">Artist Link or ID *</label>
            <input
              id="artist-link"
              type="text"
              value={artistInput}
              onChange={(e) => setArtistInput(e.target.value)}
              placeholder="https://open.spotify.com/artist/... or spotify:artist:..."
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="artist-name">Artist Name (optional)</label>
            <input
              id="artist-name"
              type="text"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              placeholder="Artist name for display"
            />
          </div>
          <div className="form-group form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Add Artist'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleLoad}
              disabled={loading}
            >
              Load User Artists
            </button>
          </div>
        </div>
      </form>

      <div className="artist-list">
        <h4>Current Artist IDs</h4>
        {artistIds.length === 0 ? (
          <p className="empty">No artist IDs loaded for this user.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Artist ID</th>
                <th>Artist Name</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {artistIds.map((artist) => (
                <tr key={artist.artistId}>
                  <td><code>{artist.artistId}</code></td>
                  <td>{artist.artistName || '—'}</td>
                  <td>{artist.addedAt ? new Date(artist.addedAt).toLocaleDateString() : '—'}</td>
                  <td>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => handleRemove(artist.artistId)}
                      disabled={loading}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminArtistIdManager;
